import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';

import { ROUTES } from '../../../constants/routes';
import Card from '../../../components/Card';
import Loading from '../../../components/Loading';
import ECharts from '../../../components/ECharts';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import { formatDisplayNumber } from '../../../utils/format';

import {
  getSeriesDataForSectorChart,
  getSectorLegendColor,
} from '../Dashboard.helpers';
import * as Styles from '../Dashboard.styles';

const TradingValueBySector = ({ isLoading, data, isDarkMode }) => {
  const { width } = useWindowDimensions();
  const [eChartRef, setEChartRef] = useState();
  const [eChartInstance, setEChartInstance] = useState();
  const { seriesData, sectorInfo } = getSeriesDataForSectorChart(
    data,
    'Top 5 Mã CK có GTGD nhiều nhất',
    'gtgd',
    width,
    'tỷ',
    1000
  );

  useEffect(() => {
    const chartInstance = eChartRef?.getEchartsInstance();
    if (chartInstance) {
      setEChartInstance(chartInstance);
      chartInstance.on('click', (param) => {
        onChartClick(param);
      });
    }
  }, [eChartRef]);

  const getChartOption = () => {
    const bgColor = isDarkMode ? '#1a1c23' : '#fff';
    const color = isDarkMode ? '#e5e7eb' : '#24262d';
    return {
      backgroundColor: bgColor,
      tooltip: {
        trigger: 'item',
      },
      series: [
        {
          type: 'pie',
          radius: ['30%', '65%'],
          data: seriesData,
          width: '100%',
          itemStyle: {
            borderColor: '#fff',
            borderWidth: 1,
          },
          label: {
            show: true,
            position: 'outside',
            color,
            fontSize: 12,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      ],
    };
  };

  const resetAllHoverChart = () => {
    if (eChartInstance) {
      for (let i = 0; i < sectorInfo.length; i++) {
        eChartInstance.dispatchAction({
          type: 'downplay',
          dataIndex: i,
        });
      }
    }
  };

  const handleMouseEnter = (id) => {
    if (eChartInstance) {
      resetAllHoverChart();
      eChartInstance.dispatchAction({
        type: 'highlight',
        dataIndex: getSectorLegendColor(sectorInfo, id).index,
      });
    }
  };

  const handleMouseLeave = () => {
    resetAllHoverChart();
  };

  const renderChartLegend = (items) => {
    const newItems = [...items];
    const total = newItems.reduce(
      (total, item) => total + Number(item.gtgd),
      0
    );
    return newItems
      .sort((a, b) => b.gtgd - a.gtgd)
      .map((item) => (
        <li
          className="flex items-start mb-3 cursor-default whitespace-nowrap"
          key={item.id}
          role="button"
          onMouseEnter={() => handleMouseEnter(item.id)}
          onMouseLeave={handleMouseLeave}
        >
          <span
            className="w-3.5 h-3.5 min-w-3.5 rounded-full inline-block mr-2"
            style={{
              background: `${getSectorLegendColor(sectorInfo, item.id).color}`,
            }}
          ></span>
          <div className="text-legend">
            <p className="text-gray-700 dark:text-gray-200 text-sm">
              {item.name}{' '}
              {item?.upTrend ? (
                <img
                  src="/images/icons/campfire.png"
                  alt="Up Trend"
                  className="w-4 h-4 min-w-4 inline-block"
                />
              ) : null}
            </p>
            <p className="whitespace-nowrap text-gray-700 dark:text-gray-200 text-sm leading-none font-bold mt-2">
              {formatDisplayNumber((parseFloat(item.gtgd) / 1000).toFixed(2))}{' '}
              tỷ (
              {item.gtgd != 0
                ? parseFloat(((Number(item.gtgd) * 100) / total).toFixed(2))
                : 0}
              %)
            </p>
          </div>
        </li>
      ));
  };

  const onChartClick = (param) => {
    const sectorId = sectorInfo[param.dataIndex].id;
    document.location.href = `${ROUTES.highLightByStockup}?sectorId=${sectorId}`;
  };

  const getChartHeight = () => {
    if ((width <= 1023 && width > 767) || width < 600) {
      return '300px';
    }
    return '450px';
  };

  return (
    <Card
      title="GTGD Theo Ngành"
      linkUrl={ROUTES.highLightByStockup}
      linkText="Xem chi tiết"
      contentClassName="mt-0"
    >
      {isLoading ? <Styles.EmptyTag /> : null}
      <Loading isLoading={isLoading} className="white" />
      {!isLoading ? (
        <>
          {!data?.length ? (
            <Box>Không có dữ liệu</Box>
          ) : (
            <div className="flex justify-between flex-col">
              <div className="w-full">
                <ECharts
                  options={getChartOption()}
                  height={getChartHeight()}
                  setEChartRef={setEChartRef}
                />
              </div>
              <div className="w-full mt-2 sector-volume-legend">
                <ul className="mt-2 grid grid-rows-2 grid-flow-col overflow-auto auto-cols-auto gap-x-3">
                  {renderChartLegend(data)}
                </ul>
              </div>
            </div>
          )}
        </>
      ) : null}
    </Card>
  );
};

export default TradingValueBySector;
